import { addDays, addMonths, endOfMonth, format, formatISO } from 'date-fns'
import { ArrowRightIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { useEmbeddedOnboarding } from 'components/hooks/employer_onboarding/useEmbeddedOnboarding'
import { EmployerOnboardingPage, useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { FormRadioGroupDS } from 'components/reusable/form/FormFields'
import { CenteredLayout } from 'pages/employer_onboarding/CenteredLayout'
import { useEmployer } from 'pages/employer_onboarding/EmployerProvider'
import { Banner, Button, InputBlock, StepNav, usePageTitle } from 'slab'
import {
  usePatchQuotingProspectsProspectIdQuotesId as useUpdateQuote,
  usePutQuotingProspectsId,
} from 'utils/web_api'

import { useQuote } from '../QuoteProvider'
import { useAPIErrorBanner } from '../useErrorBanner'
import { emitEmployerOnboardingEvent } from '../utils'
import { useProspect } from './ProspectProvider'
import { useFlags } from '../FlagProvider'

const PLAN_DATA_YEAR_ERROR_MESSAGE = "We can't generate quotes for next year at the moment."

// The values here match the month argument of the `Date` constructor, which
// is bizarrely zero-indexed, so 0 is January. ¯\_(ツ)_/¯
const MONTH_OPTIONS = [
  { value: 0, label: 'January 1st' },
  { value: 1, label: 'February 1st' },
  { value: 2, label: 'March 1st' },
  { value: 3, label: 'April 1st' },
  { value: 4, label: 'May 1st' },
  { value: 5, label: 'June 1st' },
  { value: 6, label: 'July 1st' },
  { value: 7, label: 'August 1st' },
  { value: 8, label: 'September 1st' },
  { value: 9, label: 'October 1st' },
  { value: 10, label: 'November 1st' },
  { value: 11, label: 'December 1st' },
] as const

// TODO: Check with the grammar police on our opinion on how to pluralize names
// ending with an "s". This current implementation is fairly naive.
function makePossessive (name?: string) {
  if (!name) return 'your'

  return `${name}'s`
}

function isAfterOctober15 (currentMonth: number, currentDate: number): boolean {
  return currentMonth > 9 || (currentMonth === 9 && currentDate > 15)
}

function getDefaultMonth (isAfterOctober15: boolean, currentMonth: number, currentDate: number) {
  // If after October 15th, default to January of next year
  // If after December 15th, default to February of next year
  if (isAfterOctober15) {
    const isAfterDecember15 = currentMonth === 11 && currentDate >= 15
    if (isAfterDecember15) {
      return 1
    }
    return 0
  }

  return currentDate < 15 ? currentMonth + 1 : currentMonth + 2
}

function isNextMonth (
  selectedMonth: number,
  selectedYear: number | undefined,
  currentMonth: number,
  currentYear: number
): boolean {
  // Check if this is the next month (immediately after current month)
  const isNextMonthInCurrentYear = selectedYear === currentYear && selectedMonth === currentMonth + 1
  const isNextMonthInNextYear = selectedYear === currentYear + 1 && selectedMonth === 0 && currentMonth === 11

  return isNextMonthInCurrentYear || isNextMonthInNextYear
}

function shouldDisableNextMonth (
  monthValue: number,
  selectedYear: number | undefined,
  currentYear: number,
  currentMonth: number,
  currentDate: Date
): boolean {
  if (!isNextMonth(monthValue, selectedYear, currentMonth, currentYear)) {
    return false
  }

  // Check if we're within 2 days of the end of the current month
  const currentDay = currentDate.getDate()
  const lastDayOfCurrentMonth = endOfMonth(currentDate).getDate()
  const daysUntilEndOfMonth = lastDayOfCurrentMonth - currentDay

  // Disable January 1st starting December 27th
  if (currentMonth === 11 && currentDay >= 27) {
    return true
  }

  return daysUntilEndOfMonth <= 2
}

function getValidMonths (year: number | undefined, currentDate: Date) {
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth() // 0-indexed
  let firstValidMonth = 0

  if (year === currentYear) {
    firstValidMonth = currentMonth + 1
  }

  return MONTH_OPTIONS.map(month => {
    let disabled = month.value < firstValidMonth

    // If this is the next month and we're within 2 days of month end, disable it
    if (!disabled && shouldDisableNextMonth(month.value, year, currentYear, currentMonth, currentDate)) {
      disabled = true
    }

    return {
      ...month,
      disabled,
    }
  })
}

export function BenefitsPage () {
  usePageTitle('Current benefits')

  const { flags } = useFlags()
  const { employer } = useEmployer()
  const { quote, refetch: refetchQuote } = useQuote()
  const { prospect, refetch: refetchProspect } = useProspect()
  const { isEmbedded } = useEmbeddedOnboarding()
  const { navigateToPreviousStep, navigateToStep } = useOnboardingStep()

  const { errorBanner, setError } = useAPIErrorBanner({
    fallbackMessage: 'Could not update your current benefits because of an error',
  })

  const today = useMemo(() => new Date(), [])
  const currentMonth = today.getMonth() // 0-indexed
  const currentDate = today.getDate()
  const currentYear = today.getFullYear()
  const isAfterOctober15Value = isAfterOctober15(currentMonth, currentDate)
  const canSelectCurrentYear = flags?.enableCurrentYearStartDates || !isAfterOctober15Value

  const form = useForm({
    defaultValues: {
      offers_health_insurance_today: prospect?.offers_health_insurance_today === true
        ? 'yes'
        : prospect?.offers_health_insurance_today === false
          ? 'no'
          : undefined,
      month: quote?.coverage_start_date
        ? new Date(quote.coverage_start_date).getUTCMonth()
        : getDefaultMonth(isAfterOctober15Value, currentMonth, currentDate),
      year: quote?.coverage_start_date
        ? new Date(quote.coverage_start_date).getUTCFullYear()
        : isAfterOctober15Value
          ? currentYear + 1
          : currentYear,
    },
    mode: 'onTouched',
  })

  const updateQuoteMutation = useUpdateQuote()
  const updateProspectMutation = usePutQuotingProspectsId()

  const selectedYear = form.watch('year')
  const selectedMonth = form.watch('month')

  const shouldShowPendingPricesWarning = useMemo(() => {
    const isNextYear = selectedYear === currentYear + 1
    const isBeforeNovember = currentMonth < 10 || (currentMonth === 10 && currentDate < 1)
    return isNextYear && isBeforeNovember
  }, [selectedYear, currentYear, currentMonth, currentDate])

  const skipAllowanceStrategyPage = flags?.enableNewPartnerQuotingPage === true

  useEffect(() => {
    if (selectedYear === currentYear + 1) {
      window.segment?.track(`[ER] ${selectedYear} coverage start date selected`)
    }
  }, [selectedYear, currentYear])

  const coverageStartDate = new Date(selectedYear, selectedMonth, 1)
  const employeeSelectionDate = addDays(coverageStartDate, -4)

  const planSelectionWindowName = useMemo(() => {
    const oneMonthFromToday = addDays(today, 30)
    const isWithinOneMonth = employeeSelectionDate <= oneMonthFromToday

    if (isWithinOneMonth) {
      return `Today - ${format(employeeSelectionDate, 'MMMM d')}`
    } else if (coverageStartDate.getMonth() === 0 && coverageStartDate.getDate() === 1) {
      return 'November 5 - November 30'
    } else {
      const oneMonthBeforeCoverage = new Date(coverageStartDate.getFullYear(), coverageStartDate.getMonth() - 1, 1)
      return `${format(oneMonthBeforeCoverage, 'MMMM d')} - ${format(employeeSelectionDate, 'MMMM d')}`
    }
  }, [coverageStartDate, employeeSelectionDate, today])

  /**
   * Shows a warning if the enrollment deadline is approaching.
   * Enrollment deadline is 3 days before the last day of the current month. Only shows if the current date is on or after the 15th.
   */
  const enrollmentDeadlineWarning = useMemo(() => {
    if (currentDate < 15) {
      return null
    }

    if (!isNextMonth(selectedMonth, selectedYear, currentMonth, currentYear)) {
      return null
    }

    const lastDayOfCurrentMonth = endOfMonth(today).getDate()
    const deadlineDay = lastDayOfCurrentMonth - 3
    const deadlineDate = new Date(currentYear, currentMonth, deadlineDay)

    const alternativeCoverageDate = addMonths(coverageStartDate, 1)

    return {
      selectedMonthName: format(coverageStartDate, 'MMMM'),
      deadlineDate,
      alternativeCoverageMonth: format(alternativeCoverageDate, 'MMMM'),
    }
  }, [selectedYear, selectedMonth, currentDate, currentMonth, currentYear, today, coverageStartDate])

  async function onSubmit () {
    if (!prospect) return

    const data = form.getValues()
    const coverageStartDate = formatISO(new Date(data.year!, data.month!, 1), {
      representation: 'date',
    })

    try {
      await updateProspectMutation.mutateAsync({
        id: prospect.id,
        data: {
          offers_health_insurance_today: data.offers_health_insurance_today === 'yes',
        },
      })
      await refetchProspect()

      await updateQuoteMutation.mutateAsync({
        id: quote!.id,
        prospectId: prospect.id,
        data: {
          coverage_start_date: coverageStartDate,
        },
      })

      emitEmployerOnboardingEvent('[ER] Has current benefits choice added', {
        offers_benefits: data.offers_health_insurance_today === 'yes'
      })

      await refetchQuote()

      if (shouldShowPendingPricesWarning) {
        navigateToStep(EmployerOnboardingPage.PENDING_RATES)
      } else if (data.offers_health_insurance_today === 'yes') {
        navigateToStep(quote?.has_benchmark_result ? EmployerOnboardingPage.ALLOWANCE_STRATEGY : EmployerOnboardingPage.CURRENT_BENEFITS)
      } else if (isEmbedded) {
        navigateToStep(skipAllowanceStrategyPage ? EmployerOnboardingPage.QUOTE : EmployerOnboardingPage.ALLOWANCE_STRATEGY)
      } else {
        navigateToStep(EmployerOnboardingPage.NEW_BENEFITS)
      }
    } catch (error) {
      setError(error)
    }
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading={`Tell us about ${makePossessive(
          employer?.name
        )} current benefits situation`}
        subheading='Insurance companies require that coverage starts on the 1st of the month.'
      />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <CenteredLayout.Container>
          <div className='flex flex-col lg:flex-row gap-10 items-start'>
            <div className='flex-1 space-y-6'>
              <InputBlock label='Desired coverage start date'>
                <InputBlock.Row>
                  <InputBlock.Select
                    {...form.register('month', {
                      required: 'Please select a month.',
                      valueAsNumber: true,
                      validate: (value) => {
                        const year = form.getValues('year')

                        if (value !== undefined && value <= currentMonth && year === currentYear) {
                          return 'Coverage start date must be in the future.'
                        }

                        return true
                      },
                    })}
                    label='Date'
                    error={form.formState.errors.month?.message}
                  >
                    {getValidMonths(selectedYear, today).map(
                      ({ value, label, disabled }) => (
                        <option key={value} value={value} disabled={disabled}>
                          {label}
                        </option>
                      )
                    )}
                  </InputBlock.Select>
                  <InputBlock.Select
                    {...form.register('year', {
                      required: 'Please select a year.',
                      valueAsNumber: true,
                      validate: (value) => {
                        form.trigger('month')

                        const maxAllowedYear = 2026

                        if (value && (value < currentYear || value > maxAllowedYear)) {
                          return PLAN_DATA_YEAR_ERROR_MESSAGE
                        }

                        // If after October 15th and the flag is not enabled, don't allow current year
                        if (value && value === currentYear && !canSelectCurrentYear) {
                          return `Only ${currentYear + 1} start dates are available at this time.`
                        }

                        return true
                      },
                    })}
                    label='Year'
                    error={form.formState.errors.year?.message}
                  >
                    <option
                      key={currentYear}
                      value={currentYear.toString()}
                      disabled={!canSelectCurrentYear}
                    >
                      {currentYear}
                    </option>
                    <option key={currentYear + 1} value={(currentYear + 1).toString()}>
                      {currentYear + 1}
                    </option>
                  </InputBlock.Select>
                </InputBlock.Row>
              </InputBlock>
              <FormRadioGroupDS
                control={form.control}
                name='offers_health_insurance_today'
                label={`Does ${employer?.name} currently offer health insurance?`}
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
              {errorBanner}
            </div>
            <div className='w-full lg:w-80 flex-shrink-0'>
              <StepNav className='border border-gray-200 rounded-lg p-4 [&_.w-px]:bg-thatch-500 [&_.text-gray-700]:text-thatch-500 [&_.pl-3]:pl-0.5 [&_ul.mt-3]:mt-0 [&_ul.mt-3]:pb-4'>
                <h3 className='body mb-4'>Your expected timeline:</h3>
                <StepNav.Step
                  name='Today'
                  active
                >
                  <span className='text-subdued'>
                    Finish setting up and invite your employees
                  </span>
                </StepNav.Step>
                <StepNav.Step
                  name={planSelectionWindowName}
                  active
                >
                  <span className='text-subdued'>
                    Employees pick their plans
                  </span>
                </StepNav.Step>
                <StepNav.Step
                  name={`${format(coverageStartDate, 'MMMM d')}`}
                  active
                  hideLine
                >
                  <span className='text-subdued ml-3'>
                    Coverage starts
                  </span>
                </StepNav.Step>
              </StepNav>
            </div>
          </div>
          {enrollmentDeadlineWarning && (
            <Banner
              type='warning'
              title={`Your employees need to enroll in coverage by ${format(enrollmentDeadlineWarning.deadlineDate, 'MMMM d')} to receive coverage starting ${enrollmentDeadlineWarning.selectedMonthName} 1. Otherwise, employees can receive coverage starting ${enrollmentDeadlineWarning.alternativeCoverageMonth} 1.`}
            />
          )}
        </CenteredLayout.Container>
        <CenteredLayout.Buttons>
          <Button
            variant='secondary'
            onClick={navigateToPreviousStep}
          >
            Back
          </Button>
          <Button
            disabled={form.formState.isSubmitting}
            type='submit'
            variant='primary'
            rightIcon={ArrowRightIcon}
          >
            Next step
          </Button>
        </CenteredLayout.Buttons>
      </form>
    </CenteredLayout>
  )
}
